import React from 'react';
import type { TFunction } from 'i18next';
import { AlertCircle, Cpu, Layers, Target, Plus, ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TechniqueDefinitionsService, type TechniqueDefinition } from '../../../services/technique-definitions.service';
import { localized } from '../../../utils/i18n-fields';
import { formatTechName } from '../utils';
import { FindingCard, type FindingAccent } from '../sections/FindingCard';
import { SectionDivider } from '../sections/SectionDivider';
import { CollapseBtn, CollapseBtnLeft, CollapseBtnChevron } from '../styles';
import i18n from '../../../i18n';

const DEFAULT_VISIBLE = 5;

function techIcon(name: string): LucideIcon {
  if (!name) return AlertCircle;
  const dim = name.split('.')[0]?.toUpperCase();
  if (dim === 'D3') return Cpu;
  if (dim === 'D5') return Layers;
  if (dim === 'D8') return Target;
  return AlertCircle;
}

function techAccent(severity: number): FindingAccent {
  if (severity >= 70) return 'critical';
  if (severity >= 40) return 'warning';
  return 'info';
}

/** Editorial renderer used inside Verdict (Tier 4 expanded). */
export function renderTechniquesEditorial(
  data: any,
  definitions: TechniqueDefinition[],
  t: TFunction,
  expanded: boolean,
  onToggle: () => void,
) {
  if (!data) return null;
  const techs = data.techniques_detected || [];
  if (techs.length === 0) return null;

  const dims = (data.dimensions_affected || []).join(' · ');
  const total = techs.length;
  const visible = expanded ? total : Math.min(DEFAULT_VISIBLE, total);
  const shown = techs.slice(0, visible);
  const remaining = total - visible;

  return (
    <>
      <SectionDivider
        label={t('analysis.techniques.title')}
        count={`${total} ${t('analysis.techniques.detectedSuffix')}${dims ? ` · ${dims}` : ''}`}
      />
      {shown.map((tech: any, i: number) => {
        const def = TechniqueDefinitionsService.findByName(definitions, tech.name || '');
        const dimName = localized(tech, 'dimension_name', 'dimension');
        const subdimName = localized(tech, 'subdimension_name', 'subdimension');
        const eyebrowParts = [
          tech.name?.split('.')[0]?.toUpperCase(),
          dimName ? (subdimName ? `${dimName} · ${subdimName}` : dimName) : null,
          tech.severity != null ? `${t('analysis.techniques.severityLabel')} ${tech.severity}` : null,
        ].filter(Boolean);
        const description = def ? (localized(def, 'description') as string | undefined) : undefined;
        return (
          <FindingCard
            key={i}
            icon={techIcon(tech.name || '')}
            accent={techAccent(tech.severity || 0)}
            eyebrow={eyebrowParts.join(' · ')}
            headline={formatTechName(localized(tech, 'technique_name', 'name'))}
            info={description}
            quote={tech.evidence}
          />
        );
      })}
      {remaining > 0 && (
        <CollapseBtn onClick={onToggle}>
          <CollapseBtnLeft>
            <Plus size={16} strokeWidth={2} />
            <span>{t('analysis.techniques.showMoreTechniques', { count: remaining })}</span>
          </CollapseBtnLeft>
          <CollapseBtnChevron expanded={false}>
            <ChevronDown size={14} strokeWidth={2.5} />
          </CollapseBtnChevron>
        </CollapseBtn>
      )}
      {expanded && total > DEFAULT_VISIBLE && (
        <CollapseBtn onClick={onToggle}>
          <CollapseBtnLeft>
            <ChevronDown size={16} strokeWidth={2} />
            <span>{t('analysis.techniques.hide')}</span>
          </CollapseBtnLeft>
          <CollapseBtnChevron expanded={true}>
            <ChevronDown size={14} strokeWidth={2.5} />
          </CollapseBtnChevron>
        </CollapseBtn>
      )}
    </>
  );
}

/** Backward-compat wrapper for History.tsx. */
export function renderTechniquesDetail(data: any, definitions: TechniqueDefinition[] = [], _t: any) {
  return <RenderTechniquesStatic data={data} definitions={definitions} />;
}

const RenderTechniquesStatic: React.FC<{ data: any; definitions: TechniqueDefinition[] }> = ({ data, definitions }) => {
  const [expanded, setExpanded] = React.useState(false);
  const t = i18n.t.bind(i18n) as TFunction;
  return <>{renderTechniquesEditorial(data, definitions, t, expanded, () => setExpanded(o => !o))}</>;
};

export function renderTechniquesSummary(data: any, t: any) {
  if (!data) return null;
  const count = (data.techniques_detected || []).length;
  if (count === 0) return null;
  const score = typeof data.manipulation_score === 'number' ? data.manipulation_score : (parseFloat(data.manipulation_score) || 0);
  return (
    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
      {count} {t('pipeline.techniqueCount')}{score > 0 ? ` · ${t('common.score')} ${score.toFixed(0)}` : ''}
    </span>
  );
}
